import { createSelector } from 'reselect';

const selectPageRolesDomain = state => state.PageRoles;

// Basic selectors
const makeSelectPageRoles = () =>
  createSelector(selectPageRolesDomain, substate => substate);

const makeSelectPageRolesList = () =>
  createSelector(selectPageRolesDomain, substate => substate.pageRoles);

const makeSelectPageRolesLoading = () =>
  createSelector(selectPageRolesDomain, substate => substate.loading);

const makeSelectPageRolesError = () =>
  createSelector(selectPageRolesDomain, substate => substate.error);

const makeSelectPageRolesLastUpdated = () =>
  createSelector(selectPageRolesDomain, substate => substate.lastUpdated);

const makeSelectPageRolesPagination = () =>
  createSelector(selectPageRolesDomain, substate => substate.pagination);

// Utility selectors
const makeSelectPageRoleById = (roleId) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.find(role => role.id === roleId)
  );

const makeSelectPageRolesByPage = (pageId) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role => role.pageId === pageId)
  );

const makeSelectPageRolesByTemplate = (templateId) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role => role.templateId === templateId)
  );

const makeSelectPageRolesByScope = (scopeId) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role =>
      role.scopes && role.scopes.some(scope => scope.id === scopeId)
    )
  );

const makeSelectPageRolesSearch = (searchTerm) =>
  createSelector(
    selectPageRolesDomain,
    substate => {
      if (!searchTerm) return substate.pageRoles;
      const term = searchTerm.toLowerCase();
      return substate.pageRoles.filter(role =>
        role.name.toLowerCase().includes(term) ||
        role.description.toLowerCase().includes(term) ||
        (role.pageName && role.pageName.toLowerCase().includes(term))
      );
    }
  );

const makeSelectPageRolesGroupedByPage = () =>
  createSelector(
    selectPageRolesDomain,
    substate => {
      const grouped = {};
      substate.pageRoles.forEach(role => {
        const pageId = role.pageId;
        if (!grouped[pageId]) {
          grouped[pageId] = [];
        }
        grouped[pageId].push(role);
      });
      return grouped;
    }
  );

const makeSelectPageRolesGroupedByTemplate = () =>
  createSelector(
    selectPageRolesDomain,
    substate => {
      const grouped = {};
      substate.pageRoles.forEach(role => {
        const templateId = role.templateId || 'custom';
        if (!grouped[templateId]) {
          grouped[templateId] = [];
        }
        grouped[templateId].push(role);
      });
      return grouped;
    }
  );

const makeSelectPageRolesWithScopeCount = () =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.map(role => ({
      ...role,
      scopeCount: role.scopes ? role.scopes.length : 0
    }))
  );

const makeSelectPageRolesByScopeCount = (minCount, maxCount) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role => {
      const scopeCount = role.scopes ? role.scopes.length : 0;
      return scopeCount >= minCount && (maxCount === undefined || scopeCount <= maxCount);
    })
  );

const makeSelectPageRolesByStatus = (status) =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role => role.status === status)
  );

const makeSelectPageRolesActive = () =>
  createSelector(
    selectPageRolesDomain,
    substate => substate.pageRoles.filter(role => role.status === 'active')
  );

export default makeSelectPageRoles;
export {
  makeSelectPageRoles,
  makeSelectPageRolesList,
  makeSelectPageRolesLoading,
  makeSelectPageRolesError,
  makeSelectPageRolesLastUpdated,
  makeSelectPageRolesPagination,
  makeSelectPageRoleById,
  makeSelectPageRolesByPage,
  makeSelectPageRolesByTemplate,
  makeSelectPageRolesByScope,
  makeSelectPageRolesSearch,
  makeSelectPageRolesGroupedByPage,
  makeSelectPageRolesGroupedByTemplate,
  makeSelectPageRolesWithScopeCount,
  makeSelectPageRolesByScopeCount,
  makeSelectPageRolesByStatus,
  makeSelectPageRolesActive,
};
